import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'gatsby';
import { IImage } from '@interfaces/image';
import { buildShopifyRedirectUrl } from '@src/utils/shopify-host-redirect';
import styles from './top-navigation.module.scss';

interface IProps {
  logo: IImage;
  siteTitle: string;
}

const TopNavigationComponent = ({ logo, siteTitle }: IProps) => {
  const [headerState, setHeaderState] = useState<'overlay' | 'hidden' | 'fixed'>('overlay');
  const lastScrollY = useRef(0);

  const getShopifyUrl = (path: string) => {
    return buildShopifyRedirectUrl(path) || path;
  };

  useEffect(() => {
    const downThreshold = 10;
    const upThreshold = -10;
    const topOffset = 8;

    const onScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;

      if (currentY <= topOffset) {
        setHeaderState('overlay');
      } else if (delta > downThreshold) {
        setHeaderState('hidden');
      } else if (delta < upThreshold) {
        setHeaderState('fixed');
      }

      lastScrollY.current = currentY;
    };

    lastScrollY.current = window.scrollY;
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <header
      className={`${styles.header} ${
        headerState === 'overlay'
          ? styles.headerOverlay
          : headerState === 'hidden'
            ? styles.headerHidden
            : styles.headerFixed
      }`}
    >
      <nav className={styles.nav} aria-label="Main navigation">
        <div className={styles.leftLinks}>
          <a className={styles.navLink} href={getShopifyUrl('/shop')}>
            Shop
          </a>
          <div className={styles.learnItem}>
            <button className={styles.navLinkButton} type="button" aria-haspopup="true">
              Learn
              <span className={styles.caret} aria-hidden="true" />
            </button>
            <div className={styles.dropdown} role="menu" aria-label="Learn links">
              <a className={styles.dropdownLink} href={getShopifyUrl('/about')} role="menuitem">
                About
              </a>
              <a className={styles.dropdownLink} href={getShopifyUrl('/blog')} role="menuitem">
                Blog
              </a>
              <a className={styles.dropdownLink} href={getShopifyUrl('/science')} role="menuitem">
                Science
              </a>
              <a className={styles.dropdownLink} href={getShopifyUrl('/contact')} role="menuitem">
                FAQs
              </a>
            </div>
          </div>
          <Link
            className={`${styles.navLink} ${styles.quizLink} ${headerState === 'fixed' ? styles.quizLinkCompact : ''}`}
            to="/consultation"
            aria-label="Take health quiz"
          >
            <span className={styles.quizLabel}>Take health quiz</span>
          </Link>
        </div>

        <a className={styles.logoLink} href={getShopifyUrl('/')} aria-label={`${siteTitle} homepage`}>
          <img className={styles.logoImage} src="/images/logo.svg" alt={logo?.title || siteTitle} />
        </a>

        <div className={styles.rightLinks}>
          <a className={styles.iconLink} href={getShopifyUrl('/search')} aria-label="Search">
            <SearchIcon />
          </a>
          <a className={styles.iconLink} href={getShopifyUrl('/account')} aria-label="Profile">
            <ProfileIcon />
          </a>
          <a className={styles.iconLink} href={getShopifyUrl('/cart')} aria-label="Basket">
            <BasketIcon />
          </a>
        </div>
      </nav>
    </header>
  );
};

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" className={styles.icon} aria-hidden="true">
    <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path d="M15.5 15.5L20 20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ProfileIcon = () => (
  <svg viewBox="0 0 24 24" className={styles.icon} aria-hidden="true">
    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path
      d="M4 21C4 17 7.5 14 12 14C16.5 14 20 17 20 21"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const BasketIcon = () => (
  <svg viewBox="0 0 24 24" className={styles.icon} aria-hidden="true">
    <path
      d="M6 8L8 3H16L18 8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M3.5 8H20.5C20.5 8 21 8 21 8.5V10C21 10.5 20.5 10.5 20.5 10.5H3.5C3.5 10.5 3 10.5 3 10V8.5C3 8 3.5 8 3.5 8Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M4.5 10.5L5.5 20C5.5 20.5 6 21 6.5 21H17.5C18 21 18.5 20.5 18.5 20L19.5 10.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

export default TopNavigationComponent;
